import { createElement } from "react";
import { FileText } from "lucide-react";
import type { Command, IOmniSearchService } from "../../core";
import { command } from "../../core";
import type { Candidate, CommandPaletteSource } from "../types";

/**
 * Omni-search source — cross-model record lookup in the palette's default
 * (no-prefix) mode (PC-10). Typing a record's name surfaces matching records
 * from every model that opted in via `@api.model(omni_search=True)`.
 *
 * Backend gating: the `/api/web/omni-search` endpoint scans only opted-in
 * models and applies record-rule RBAC, so this source stays dumb — it just
 * renders whatever hits come back, grouped under "Records".
 *
 * Debounce + cancellation are handled upstream: `CommandPalette` debounces
 * every `engine.search`, and the engine aborts the prior in-flight search,
 * handing us its `signal` — forwarded to the HTTP call so a superseded
 * keystroke's request is cancelled mid-flight.
 */

// Below this length the query is too broad to be useful and would spam the
// backend on the first keystroke or two. Mirrors typical autocomplete gates.
const MIN_QUERY_LENGTH = 2;

// Number of app-color tile slots in the theme (`--app-color-0..10`, wired via
// `data-color` in _chrome.scss). Keep in sync with that palette.
const TILE_COLOR_COUNT = 11;

/**
 * Map a model key to a stable tile-colour index (0..TILE_COLOR_COUNT-1) so
 * every record of the same model shows the same colour and different models
 * are visually distinguishable in the palette — deterministic, no per-model
 * config. Simple polynomial string hash (same idea as `String.hashCode`).
 */
export function modelColorIndex(modelKey: string): number {
    let hash = 0;
    for (let i = 0; i < modelKey.length; i += 1) {
        hash = (hash * 31 + modelKey.charCodeAt(i)) >>> 0;
    }
    return hash % TILE_COLOR_COUNT;
}

export function buildOmniSearchSource(
    omniSearch: IOmniSearchService,
    dispatch: (cmd: Command) => Promise<unknown>,
): CommandPaletteSource {
    return {
        key: "records",
        label: "Records",
        // No prefix → participates in the default (free-text) search mode.
        async search(query: string, signal?: AbortSignal): Promise<Candidate[]> {
            const q = query.trim();
            if (q.length < MIN_QUERY_LENGTH) return [];

            let hits;
            try {
                hits = await omniSearch.search(q, { signal });
            } catch {
                // Aborted (superseded keystroke) or transport error → no rows.
                return [];
            }

            return hits.map((hit) => ({
                id: `record:${hit.model_key}:${hit.record_uuid}`,
                title: hit.display_name,
                subtitle: hit.model_label,
                keywords: [hit.display_name, hit.model_label],
                // The server already matched + ranked + capped; keep a constant
                // positive score so the engine preserves that order (it drops
                // score === 0 rows and sorts desc, so equal scores are stable).
                score: 1,
                // Colour the icon tile by model key so each model is visually
                // distinct and consistent across hits (mirrors the navigate
                // source's app-coloured tiles).
                accentColorIndex: modelColorIndex(hit.model_key),
                icon: createElement(FileText, { size: 11 }),
                onPick: () => {
                    void dispatch(
                        command("OPEN_RECORD", {
                            modelKey: hit.model_key,
                            recordRef: hit.record_uuid,
                        }),
                    );
                },
            }));
        },
    };
}
